'use client';

import { useAuth0 } from '@auth0/auth0-react';
import { Button, ButtonProps } from '@petsmart-ui/sparky';
import { usePathname, useSearchParams } from 'next/navigation';
import { ReactElement, useCallback } from 'react';

import type { PostLoginIntent } from '../components/Auth0';
import { AccountDrawerType, type SignInDrawerProps } from '../types/pageLayout';
import { getAuth0LoginRedirectOptions, isAuth0RestrictedCheckoutReturnTo } from '../utils/getAuth0LoginRedirectOptions';
import { getAuth0ReturnTo, isSamePageReturnTo } from '../utils/getAuth0ReturnTo';
import { useCart } from './useCart';
import { useDecisions } from './useDecisions';
import { usePageLayout } from './usePageLayout';

type SignInCreateAccountButtonProps = Omit<Exclude<ButtonProps, { href: string }>, 'href'>;

type AuthSignInRequest = {
    legacyDrawerProps?: SignInDrawerProps;
    postLoginIntent?: PostLoginIntent;
};

export function SignInCreateAccountButton(props: Readonly<SignInCreateAccountButtonProps>): ReactElement {
    return <Button data-testid="signInCreateAccount" text="Sign in / Create account" {...props} />;
}

export function useAuthSignInClick(returnTo?: string) {
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { openAccountDrawer, setSignInDrawerProps } = usePageLayout();
    const { cart } = useCart();
    const isAuth0Enabled = useDecisions('auth0');
    const { loginWithRedirect } = useAuth0();
    const search = searchParams?.toString() ?? '';
    const currentPath = getAuth0ReturnTo({ pathname, search });
    const resolvedReturnTo = returnTo ?? currentPath;
    // Regular header signup still marks the current page as implicit so registration lands on
    // About You. Cart, checkout, and product pages return to the same page.
    const returnToIsImplicit = returnTo === undefined && !isSamePageReturnTo(resolvedReturnTo);
    // Header and Treats omit `returnTo`. Restricted `ext-*` flags require an explicit cart/checkout destination.
    const includeCartCategoryParams = returnTo !== undefined && isAuth0RestrictedCheckoutReturnTo(resolvedReturnTo);

    const handleSignInClick = useCallback(
        ({ legacyDrawerProps, postLoginIntent }: AuthSignInRequest = {}) => {
            if (isAuth0Enabled) {
                return void loginWithRedirect(
                    getAuth0LoginRedirectOptions({
                        returnTo: resolvedReturnTo,
                        returnToIsImplicit,
                        cart: includeCartCategoryParams ? cart : undefined,
                        flowEntry: currentPath,
                        postLoginIntent,
                    })
                );
            }

            if (legacyDrawerProps) {
                setSignInDrawerProps(legacyDrawerProps);
            }
            openAccountDrawer(AccountDrawerType.SIGN_IN);
        },
        [
            cart,
            currentPath,
            includeCartCategoryParams,
            isAuth0Enabled,
            loginWithRedirect,
            openAccountDrawer,
            resolvedReturnTo,
            returnToIsImplicit,
            setSignInDrawerProps,
        ]
    );

    return { isAuth0Enabled, handleSignInClick };
}
